import React, { ErrorInfo } from "react";
import { Notifier } from "@airbrake/browser";
import { Box, Button, Chip, Container, Paper, Stack, Typography } from "@mui/material";
import RefreshIcon from '@mui/icons-material/Refresh';
import HomeIcon from '@mui/icons-material/Home';
import FlowentiLogo from "image/Logo.svg";

class ErrorBoundary extends React.Component<React.PropsWithChildren> {
  airbrake: Notifier;
  state: { error: undefined | string; hasError: boolean } = {
    error: undefined,
    hasError: false,
  };
  private promiseRejectionHandler = (event: PromiseRejectionEvent) => {
    this.setState({
      error: String(event.reason),
    });
  };
  constructor(props: {} | Readonly<{}>) {
    super(props);
    this.state = { hasError: false, error: undefined };
    this.airbrake = new Notifier({
      environment: process.env.NODE_ENV,
      host: process.env.REACT_APP_ERRBIT_URL,
      projectId: 1,
      projectKey: process.env.REACT_APP_ERRBIT_PROJECT_KEY,
    });
    this.airbrake.addFilter((notice) => {
      notice.context.version = process.env.REACT_APP_VERSION;
      return notice;
    });
  }

  componentDidCatch(error: Error, info: ErrorInfo) {

    // Display fallback UI
    this.setState({ hasError: true });
    // Send error to Airbrake
    this.airbrake.notify({
      error: error,
      params: { info: info},
    });
  }
  componentDidMount() {
    // Add an event listener to the window to catch unhandled promise rejections & stash the error in the state
    window.addEventListener("unhandledrejection", this.promiseRejectionHandler);
  }

  componentWillUnmount() {
    window.removeEventListener(
      "unhandledrejection",
      this.promiseRejectionHandler
    );
  }
  render() {
    if (this.state.hasError || this.state.error) {
      // console.log(this.state.error)
      // You can render any custom fallback UI
      return <>
        <Container
          maxWidth="sm"
          sx={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 6,
            // background: "linear-gradient(135deg, #fdfbfb 0%, #ebedee 100%)",
          }}
        >
          {/* Logo + Nome App */}
          <Box display="flex" alignItems="center" mb={4}>
            <Box
              component="img"
              height="70px"
              width="70px"
              src={FlowentiLogo}
              alt="Flowenti Logo"
              sx={{
                mr: 2,
                borderRadius: "16px",
                // boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              }}
            />
            <Typography variant="h1" fontWeight={400} color="primary">
              Flowenti
            </Typography>
          </Box>

          {/* Card principale */}
          <Paper
            elevation={4}
            sx={{
              p: 5,
              textAlign: "center",
              borderRadius: 4,
              maxWidth: 480,
              width: "100%",
            }}
          >
            <Stack spacing={3} alignItems="center">
              <Typography
                variant="h4"
                component="h1"
                fontWeight="bold"
                color="primary.main"
              >
                Uoops!
              </Typography>

              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ fontStyle: "italic" }}
              >
                Qualcosa è andato storto!
              </Typography>

              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ maxWidth: 380, lineHeight: 1.6 }}
              >
                Refresha la pagina oppure utilizza i bottoni qua sotto per riprende a usare l'applicazione
              </Typography>

              {/* Bottoni */}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{ mt: 2 }}
              >
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={() => window.location.reload()}
                  sx={{
                    minWidth: 140,
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Refresh
                </Button>
                <Button
                  variant="contained"
                  startIcon={<HomeIcon />}
                  onClick={() => (window.location.href = "/")}
                  sx={{
                    minWidth: 140,
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Go Home
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Container>
      </>
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
